import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { API_URL, MARKETING_URL, api } from "../lib/api";
import { Icon, Logo } from "./icons";
import { cx } from "./ui";
import type { AffiliateRole } from "../lib/types";
import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";
import { LanguageSwitch } from "./LanguageSwitch";

/*
 * The shell: a rail on desktop, a row of tabs on a phone.
 *
 * Five destinations — six with the programme — and no drawer, which is why
 * this is simpler than the customer dashboard's `AppLayout` rather than a
 * trimmed copy of it. A hamburger that hides five links behind a tap is chrome
 * standing in for chrome. Past 375px the phone row scrolls sideways rather than
 * wrapping, so the header keeps one height whichever tab is active.
 */

/* `label` is a key under the `nav` namespace; NavItem reads it through `t`. */
const NAV: { to: string; label: ParseKeys<"nav">; end?: boolean }[] = [
  { to: "/", label: "overview", end: true },
  { to: "/links", label: "links" },
  /* Between Links and Referrals on purpose: it is the screen you open *after*
     making links and *before* asking who signed up, which is the order the
     questions actually come in. */
  /* Between the things to share and the things that measure them, which is the
     order the pages themselves run in. */
  { to: "/promote", label: "promote" },
  { to: "/traffic", label: "traffic" },
  { to: "/referrals", label: "referrals" },
  { to: "/payouts", label: "payouts" },
];

/**
 * The one entry an ordinary affiliate never sees.
 *
 * Shown to `admin` and to `finance`, who see different things behind it — the
 * section's own nav makes that split, not this one. Hidden rather than
 * disabled, and hiding it is a convenience rather than the boundary: every
 * route behind it checks the role on the server, which is the rule
 * CONVENTIONS.md states for `useCan` in the other two dashboards. Somebody
 * typing `/admin` without a role gets a 403 from the API, not a screen.
 */
const ADMIN_NAV = { to: "/admin", label: "programme" as const };

/**
 * Running the programme, as a separate place rather than a sixth tab.
 *
 * These were a sub-nav inside the section, under a header still showing the
 * affiliate's own five tabs. That meant two rows of navigation and, worse, two
 * *contexts* on screen at once: "Payouts" appeared twice, meaning the money you
 * are owed on one row and everybody's money on the other. It is one word,
 * duplicated, meaning two different things a click apart.
 *
 * So the header swaps. Inside `/admin` these replace the affiliate's own tabs
 * and the section's sub-nav goes away entirely; a labelled way back is the
 * first item, because a context you cannot obviously leave is a trap.
 */
const PROGRAMME_NAV: { to: string; label: ParseKeys<"nav">; end?: boolean; admin?: boolean }[] = [
  { to: "/admin", label: "overview", end: true },
  { to: "/admin/affiliates", label: "affiliates" },
  { to: "/admin/payouts", label: "payouts" },
  { to: "/admin/terms", label: "terms", admin: true },
];

export function AppLayout({ displayName, role }: { displayName: string; role: AffiliateRole }) {
  const client = useQueryClient();
  const location = useLocation();
  const { t } = useTranslation("nav");

  /*
   * Which dashboard this is. Prefix-matched on the path rather than tracked in
   * state: the browser's back button and a pasted URL both have to land in the
   * right context, and state would only be right for a click.
   */
  const inProgramme = location.pathname === "/admin" || location.pathname.startsWith("/admin/");

  /*
   * Named, and stated as what may pass rather than what may not.
   *
   * This was `role === "member" ? NAV : [...NAV, ADMIN_NAV]`, which is the same
   * thing for the three roles that exist and **fails open** for everything
   * else. Any value the client does not recognise — a `role` missing from the
   * response because the API is a deploy behind, a null, a role added later —
   * is not `"member"`, so the programme entry appeared for everybody.
   *
   * Reproduced by serving `/me` without the field: the entry showed on every
   * screen, for an account with no role at all.
   *
   * A hidden link was never the boundary — `AdminLayout` redirects and the API
   * answers 403 — so nothing was reachable. What it cost is confidence, which
   * on a dashboard about money is not nothing: an affiliate seeing a menu they
   * cannot open has to wonder what else is not for them.
   */
  const canRunProgramme = role === "admin" || role === "finance";

  const nav = inProgramme
    ? PROGRAMME_NAV.filter((item) => !item.admin || role === "admin")
    : canRunProgramme
      ? [...NAV, ADMIN_NAV]
      : NAV;

  async function signOut() {
    // Best-effort. The cookie is cleared by the response, and a failure here
    // leaves a session the visitor believes is gone — so the reload happens
    // either way and the worst case is a session that is still live on the
    // server and unreachable from this browser.
    await api("/v1/affiliate/auth/session", { method: "DELETE" }).catch(() => {});
    client.clear();
    window.location.assign("/");
  }

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-10 border-b border-line bg-canvas/95 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-4 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-2">
            <Logo className="size-6 shrink-0" />
            <span className="truncate text-[13px] font-medium text-fg">
              {inProgramme ? t("brand.programme") : t("brand.affiliates")}
            </span>
            {/* The one piece of chrome that says which dashboard this is, on the
                wordmark where somebody already looks to answer that.

                The actual role and not the word "admin": a finance holder sees
                a shorter set of tabs than an admin does, and being told which
                of the two they are is what explains why Terms is not there. */}
            {inProgramme && canRunProgramme && (
              <span className="hidden rounded-full bg-accent-soft px-2 py-0.5 text-[11px] text-accent sm:inline">
                {t(`role.${role}`)}
              </span>
            )}
          </div>

          {/* The rail, on anything wide enough to hold it beside the wordmark. */}
          <nav className="hidden flex-1 items-center gap-1 sm:flex">
            {inProgramme && <BackToDashboard />}
            {nav.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 sm:ml-0">
            {/* The name, so somebody with two Maxlayer accounts can tell which
                one this dashboard is signed in as. Hidden on a phone, where the
                sign-out control is the thing worth the width. */}
            <span className="hidden max-w-[12rem] truncate text-[13px] text-fg-muted md:block">
              {displayName}
            </span>
            <button
              type="button"
              onClick={() => void signOut()}
              className="inline-flex size-11 items-center justify-center rounded-md text-fg-muted transition-colors duration-200 ease-smooth hover:bg-raised hover:text-fg sm:size-8"
              aria-label={t("signOut")}
            >
              <Icon name="logout" />
            </button>
          </div>
        </div>

        {/* On a phone the nav moves below the wordmark rather than into a
            drawer: four links fit across 375px, and a tap to reveal a tap is
            worse than a second row. It scrolls sideways if a label ever grows. */}
        <nav className="scroll-shadow-x flex gap-1 overflow-x-auto border-t border-line px-4 py-1.5 [--scroll-shadow-bg:var(--color-canvas)] sm:hidden">
          {inProgramme && <BackToDashboard />}
          {nav.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </nav>
      </header>

      {/*
       * The wash sits on the scrolling region rather than on `body`, so it stays
       * anchored to the top of the content instead of following the viewport.
       * At 3% it is barely a colour — enough that the top of a white page is not
       * a flat sheet, and not enough for anybody to notice it as a decision.
       */}
      <div className="page-wash flex-1">
        <main className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
          <Outlet />
        </main>
      </div>

      <footer className="border-t border-line">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-4 text-[12px] text-fg-subtle sm:px-6">
          <span>{t("footer")}</span>
          {MARKETING_URL && (
            <a
              className="inline-flex items-center gap-1 transition-colors duration-200 ease-smooth hover:text-accent"
              href={MARKETING_URL}
              target="_blank"
              rel="noreferrer"
            >
              maxlayer.cloud
              <Icon name="external" className="size-3" />
            </a>
          )}
          {/* The API origin, which is the one piece of configuration a support
              conversation ever needs: "signed out on every page" is almost
              always this pointing at a different registrable domain. */}
          <LanguageSwitch className="ml-auto text-[12px]" />
          <span className="font-mono">{new URL(API_URL).host}</span>
        </div>
      </footer>
    </div>
  );
}

function NavItem({ to, label, end }: { to: string; label: ParseKeys<"nav">; end?: boolean }) {
  const { t } = useTranslation("nav");
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cx(
          "rounded-md px-2.5 py-1.5 text-[13px] whitespace-nowrap transition-colors duration-200 ease-smooth",
          // The accent marks where you can go. An active tab is the one place
          // on this screen it appears, which is what keeps it meaning that.
          isActive ? "bg-accent-soft text-accent" : "text-fg-muted hover:bg-raised hover:text-fg",
        )
      }
    >
      {t(label)}
    </NavLink>
  );
}

/**
 * The way out of the programme, and it is deliberately the first thing.
 *
 * A context somebody cannot obviously leave is a trap, and this one is easy to
 * arrive in and easy to forget you are in — the tabs look like an affiliate
 * dashboard's tabs, because they are laid out like one. The separator after it
 * is what makes the rest of the row read as a group rather than as five peers.
 */
function BackToDashboard() {
  const { t } = useTranslation("nav");
  return (
    <>
      <Link
        to="/"
        className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[13px] whitespace-nowrap text-fg-muted transition-colors duration-200 ease-smooth hover:bg-raised hover:text-fg"
      >
        <span aria-hidden="true">←</span>
        {t("yourDashboard")}
      </Link>
      <span className="mx-1 h-4 w-px shrink-0 bg-line" aria-hidden="true" />
    </>
  );
}
